import { Loading, MessageBox } from "element-ui";
import {
  useShowErrorMessage,
  useShowSuccessMessage,
} from "../use-notifications";
import useHttpRequest from "../use-http-request";
import { useRouter } from "vue-router/composables";
import { Ref } from "vue";
import { DraftStorage } from "@/helpers/onlineAnswerSheet/draftStorage";
import CourseWork from "@/interfaces/courseWork.interface";

export const useSubmitCourseWork = ({
  courseWork,
  saveStudentChanges,
  saveStudentFinalChanges,
  isUploadAnswerSheetDialogVisible,
  submissionType,
  draftStorage,
}: {
  courseWork: Ref<CourseWork | null>;
  saveStudentChanges: () => Promise<string | null>;
  saveStudentFinalChanges: () => Promise<boolean | null>;
  isUploadAnswerSheetDialogVisible: Ref<boolean>;
  submissionType: Ref<"Online" | "File Upload" | undefined>;
  draftStorage: Ref<DraftStorage | undefined>;
}) => {
  const router = useRouter();

  const onSubmitAnswerSheetIntent = () => {
    if (!courseWork.value?.is_answersheet_file_upload_allowed) {
      submissionType.value = "Online";
      isUploadAnswerSheetDialogVisible.value = true;
      return;
    }

    MessageBox.alert(
      "Please Choose a method of submitting your answer sheet, you can either upload the Answer Sheet file you downloaded or Submit the Online Answer sheet.",
      "Choose Submission Method",
      {
        confirmButtonText: "Online Answers",
        cancelButtonText: "Upload File",
        showCancelButton: true,
        distinguishCancelAndClose: true,
        closeOnClickModal: true,
        closeOnPressEscape: true,

        // type: "warning",
        callback: (action) => {
          if (action == "confirm") {
            // this.uploadType = "ONLINE";
            submissionType.value = "Online";
            isUploadAnswerSheetDialogVisible.value = true;
          } else if (action == "cancel") {
            submissionType.value = "File Upload";
            isUploadAnswerSheetDialogVisible.value = true;
          }
        },
      }
    );
  };

  const onSubmitOnlineAnswerSheet = async () => {
    let loadingIndicator: ReturnType<typeof Loading.service> | undefined;
    try {
      loadingIndicator = Loading.service({
        lock: true,
        text: "Submitting...",
        background: "rgba(0, 0, 0, 0.7)",
      });

      const isSaved = await saveStudentChanges();

      if (!isSaved) {
        return useShowErrorMessage(
          "Unable to Save Changes",
          "An unexpected Error occurred, please try again"
        );
      }

      const finalChangesSaved = await saveStudentFinalChanges();

      if (!finalChangesSaved) {
        return useShowErrorMessage(
          "Unable to Save Changes",
          "An unexpected Error occurred, please try again"
        );
      }

      const request = await useHttpRequest<{
        success: boolean;
        message: string;
      }>({
        method: "POST",
        url: "course-work/submit-online-answer-sheet",
        body: {
          course_work_id: courseWork.value?.course_work_id,
        },
        loadingProperty: null,
        showSuccessMessage: false,
        errorLoadingProperty: null,
        showFullScreenLoader: false,
      });

      if (!request) {
        return;
      }

      if (
        request.success &&
        request.message == "ONLINE ANSWER SHEET SUBMITTED SUCCESSFULLY"
      ) {
        useShowSuccessMessage(
          "Course Work Submitted",
          "Course Work Answers successfully submitted"
        );

        draftStorage.value?.clear();

        return router.push({
          name: "Courseworks",
        });
      }
    } catch (error) {
      //Do nothing for now
    } finally {
      if (loadingIndicator) {
        loadingIndicator.close();
      }
    }
  };

  const onSubmitFileUploadAnswerSheet = async (path: {
    url: string;
    key: string;
    file_name: string;
  }) => {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
    }>({
      method: "POST",
      url: "course-work/submit-file-upload-answer-sheet",
      body: {
        course_work_id: courseWork.value?.course_work_id,
        answer_sheet_path_url: path.url,
        answer_sheet_path_key: path.key,
        file_name: path.file_name,
      },
      loadingProperty: null,
      showSuccessMessage: false,
      errorLoadingProperty: null,
      showFullScreenLoader: true,
      fullScreenLoaderMessage: "Submitting...",
    });

    if (!request) {
      return;
    }

    if (
      request.success &&
      request.message == "ANSWER SHEET SUBMITTED SUCCESSFULLY"
    ) {
      useShowSuccessMessage(
        "Course Work Submitted",
        "Course Work Answers successfully submitted"
      );

      draftStorage.value?.clear();

      return router.push({
        name: "Courseworks",
      });
    }
  };

  return {
    onSubmitOnlineAnswerSheet,
    onSubmitFileUploadAnswerSheet,
    onSubmitAnswerSheetIntent,
  };
};
